import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import Navbar from "../frontend/backend/navbar.js";
import Footer from "../frontend/backend/footer.js";
import Chatbox from "../frontend/backend/chatbox.js";
import HeaderRight from "../frontend/backend/header_right.js";
import LeftSidebar from "../frontend/backend/leftSidebar.js";
import axios from "axios";
import ReactPaginate from "react-paginate";
import styled from "styled-components";
import apiConfig from '../apiConfig';

const LuckyExchangeSummery = () => {

  const [loading, setLoading]           = useState(true);
  const [detailData, setDetailData]     = useState([]);
  const [matchSummary, setMatchSummary] = useState([]);
  const [matchSort, setMatchSort]       = useState({ key: 'lastBetAt', direction: 'desc' });

  const [summary, setSummary] = useState({
    totalRecords: 0, totalBet: 0, totalWin: 0, totalLoss: 0,
    netProfitLoss: 0, openBets: 0, settledBets: 0, uniquePlayers: 0,
  });

  const [pageCount, setPageCount]     = useState(1);
  const [activeRange, setActiveRange] = useState('today');
  const [startDate, setStartDate]     = useState('');
  const [endDate, setEndDate]         = useState('');

  // Filters
  const [statusFilter, setStatusFilter]   = useState('');
  const [sportFilter, setSportFilter]     = useState('');

  const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });
  const currentPage = useRef(1);
  const limit = 50;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toLocalDateString = (d) => {
    const y  = d.getFullYear();
    const mo = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${mo}-${day}`;
  };

  const formatDate = (iso) => {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')}.${d.getFullYear()} `
         + `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };

  const getDateRange = (range) => {
    const now      = new Date();
    const todayStr = toLocalDateString(now);
    if (range === 'today')   return { start: todayStr, end: todayStr };
    if (range === 'weekly')  {
      const s = new Date(now); s.setDate(now.getDate() - 7);
      return { start: toLocalDateString(s), end: todayStr };
    }
    if (range === 'monthly') {
      const s = new Date(now.getFullYear(), now.getMonth(), 1);
      return { start: toLocalDateString(s), end: todayStr };
    }
    return { start: '', end: '' };
  };

  // ── Fetch ─────────────────────────────────────────────────────────────────
  const fetchData = async (
    start, end,
    pageOnly  = false,
    srchStatus = statusFilter,
    srchSport  = sportFilter,
  ) => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiConfig.baseURL}/api/agent/LuckyExchangeReport`, {
        params: {
          startDate:   start     || undefined,
          endDate:     end       || undefined,
          page:        currentPage.current,
          limit,
          skipSummary: pageOnly ? 'true' : 'false',
          status:      srchStatus || undefined,
          sportName:   srchSport  || undefined,
        }
      });

      setDetailData(res.data.data || []);

      if (!pageOnly) {
        setSummary({
          totalRecords:  res.data.totalRecords  || 0,
          totalBet:      res.data.totalBet      || 0,
          totalWin:      res.data.totalWin      || 0,
          totalLoss:     res.data.totalLoss     || 0,
          netProfitLoss: res.data.netProfitLoss || 0,
          openBets:      res.data.openBets      || 0,
          settledBets:   res.data.settledBets   || 0,
          uniquePlayers: res.data.uniquePlayers || 0,
        });
        setMatchSummary(res.data.matchSummary || []);
      }

      setPageCount(res.data.pageCount || 1);
    } catch (err) {
      console.error("LuckyExchange fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Initial load ──────────────────────────────────────────────────────────
  useEffect(() => {
    const { start, end } = getDateRange('today');
    setStartDate(start);
    setEndDate(end);
    fetchData(start, end);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRangeClick = (range) => {
    setActiveRange(range);
    if (range === 'custom') return;
    const { start, end } = getDateRange(range);
    setStartDate(start);
    setEndDate(end);
    currentPage.current = 1;
    fetchData(start, end, false, statusFilter, sportFilter);
  };

  const handleSearch = () => {
    currentPage.current = 1;
    fetchData(startDate, endDate, false, statusFilter, sportFilter);
  };

  const handleClear = () => {
    setStatusFilter('');
    setSportFilter('');
    currentPage.current = 1;
    fetchData(startDate, endDate, false, '', '');
  };

  const handlePageClick = (e) => {
    currentPage.current = e.selected + 1;
    fetchData(startDate, endDate, true, statusFilter, sportFilter);
  };

  // ── Match-wise sort (client-side) ─────────────────────────────────────────
  const sortedMatches = [...matchSummary].sort((a, b) => {
    const dir = matchSort.direction === 'asc' ? 1 : -1;
    if (matchSort.key === 'match_name' || matchSort.key === 'sport_name')
      return dir * String(a[matchSort.key]).localeCompare(String(b[matchSort.key]));
    if (matchSort.key === 'lastBetAt')
      return dir * (new Date(a.lastBetAt) - new Date(b.lastBetAt));
    return dir * ((a[matchSort.key] || 0) - (b[matchSort.key] || 0));
  });

  const handleMatchSort = (key) => {
    setMatchSort(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc',
    }));
  };

  const MatchSortIcon = ({ field }) => {
    if (matchSort.key !== field) return <span style={{ color: '#888' }}> ↕</span>;
    return <span> {matchSort.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  // ── Detail sort (client-side) ─────────────────────────────────────────────
  const sortDetail = (key) => {
    const dir = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const sorted = [...detailData].sort((a, b) => {
      if (key === 'amount') return dir === 'asc' ? a.amount - b.amount : b.amount - a.amount;
      return dir === 'asc'
        ? new Date(a.createdAt) - new Date(b.createdAt)
        : new Date(b.createdAt) - new Date(a.createdAt);
    });
    setDetailData(sorted);
    setSortConfig({ key, direction: dir });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return <span style={{ color: '#888' }}> ↕</span>;
    return <span> {sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;
  };

  // ── Status badge ──────────────────────────────────────────────────────────
  const StatusBadge = ({ status }) => {
    const map = {
      win:  { bg: '#dcfce7', color: '#166534', label: '✓ Win'  },
      lost: { bg: '#fee2e2', color: '#991b1b', label: '✗ Lost' },
      open: { bg: '#fef3c7', color: '#92400e', label: '⏳ Open' },
    };
    const s = map[status] || { bg: '#f1f5f9', color: '#475569', label: status };
    return (
      <span style={{
        padding: '2px 8px', borderRadius: '10px',
        fontSize: '11px', fontWeight: '600',
        background: s.bg, color: s.color,
      }}>
        {s.label}
      </span>
    );
  };

  // ── Back/Lay badge ────────────────────────────────────────────────────────
  const BackLayBadge = ({ val }) => {
    const isBack = val === 'b';
    return (
      <span style={{
        padding: '2px 7px', borderRadius: '10px',
        fontSize: '11px', fontWeight: '600',
        background: isBack ? '#dbeafe' : '#fce7f3',
        color:      isBack ? '#1e40af' : '#9d174d',
      }}>
        {isBack ? 'Back' : 'Lay'}
      </span>
    );
  };

  // ── Styles ────────────────────────────────────────────────────────────────
  const MyPaginate = styled(ReactPaginate).attrs({ activeClassName: 'active' })`
    margin: 1rem 0;
    display: flex;
    flex-direction: row;
    justify-content: center;
    list-style-type: none;
    padding: 0;
    gap: 4px;
    li a { border-radius: 6px; padding: 4px 10px; border: 1px solid #334155; cursor: pointer; color: #000; font-size: 13px; }
    li.active a { background: #f59e0b; border-color: #f59e0b; color: #000; font-weight: 700; }
    li.disabled a { color: #aaa; cursor: default; }
  `;

  const thStyle = {
    color: '#000', background: '#fff',
    cursor: 'pointer', whiteSpace: 'nowrap',
    fontWeight: '600', fontSize: '13px',
  };

  const cardStyle = (border) => ({
    background: '#fff', border: `1px solid ${border}`, borderRadius: '12px',
  });

  const rangeButtons = ['today', 'weekly', 'monthly', 'custom'];

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
      <Navbar /><Chatbox /><HeaderRight /><LeftSidebar />

      <div className="content-body">
        <div className="container-fluid">
          <div className="col-lg-12">

            {/* ── Header ── */}
            <div className="d-flex justify-content-between align-items-center mb-4 mt-3">
              <h4 className="mb-0">🎯 Lucky Exchange Summary</h4>
              <div className="d-flex gap-2">
                <Link to="/bet-summery"      className="btn btn-outline-secondary btn-sm">Casino</Link>
                <Link to="/exchange-summery" className="btn btn-outline-secondary btn-sm">Exchange</Link>
              </div>
            </div>

            {/* ── Date Range Buttons ── */}
            <div className="mb-3 d-flex gap-2 flex-wrap">
              {rangeButtons.map(r => (
                <button key={r}
                  onClick={() => handleRangeClick(r)}
                  style={{
                    padding: '6px 14px', borderRadius: '20px', border: 'none',
                    cursor: 'pointer', fontSize: '13px', fontWeight: '600',
                    background: activeRange === r ? '#f59e0b' : '#fff', color: '#000',
                  }}>
                  {r.charAt(0).toUpperCase() + r.slice(1)}
                </button>
              ))}
            </div>

            {/* ── Custom Date ── */}
            {activeRange === 'custom' && (
              <div className="row mb-3 g-2 align-items-end">
                <div className="col-md-4">
                  <input type="date" value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="form-control form-control-sm"
                    style={{ background: '#fff', color: '#000', border: '1px solid #334155' }} />
                </div>
                <div className="col-auto" style={{ color: '#000' }}>→</div>
                <div className="col-md-4">
                  <input type="date" value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    className="form-control form-control-sm"
                    style={{ background: '#fff', color: '#000', border: '1px solid #334155' }} />
                </div>
                <div className="col-md-2">
                  <button
                    onClick={() => { currentPage.current = 1; fetchData(startDate, endDate, false, statusFilter, sportFilter); }}
                    className="btn btn-sm w-100"
                    style={{ background: '#f59e0b', color: '#000', fontWeight: '700' }}>
                    Search
                  </button>
                </div>
              </div>
            )}

            {/* ── Filters ── */}
            <div className="row mb-3 g-2 align-items-end">
              <div className="col-md-3">
                <label style={{ color: '#000', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Status</label>
                <select value={statusFilter}
                  onChange={e => setStatusFilter(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ background: '#fff', color: '#000', border: '1px solid #334155' }}>
                  <option value="">All Status</option>
                  <option value="open">Open</option>
                  <option value="win">Win</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
              <div className="col-md-4">
                <label style={{ color: '#000', fontSize: '12px', marginBottom: '4px', display: 'block' }}>Sport Name</label>
                <input type="text" value={sportFilter}
                  onChange={e => setSportFilter(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  placeholder="e.g. Cricket..."
                  className="form-control form-control-sm"
                  style={{ background: '#fff', color: '#000', border: '1px solid #334155' }} />
              </div>
              <div className="col-md-2">
                <button onClick={handleSearch} className="btn btn-sm w-100"
                  style={{ background: '#f59e0b', color: '#000', fontWeight: '700' }}>
                  🔍 Search
                </button>
              </div>
              <div className="col-md-2">
                <button onClick={handleClear} className="btn btn-sm w-100"
                  style={{ background: '#fff', color: '#000', border: '1px solid #334155' }}>
                  ✕ Clear
                </button>
              </div>
            </div>

            {/* ── Active Filters ── */}
            {(statusFilter || sportFilter) && (
              <div className="mb-2" style={{ fontSize: '12px', color: '#000' }}>
                🔍 Filtering by:
                {statusFilter && <span className="ms-2 badge" style={{ background: '#78350f', color: '#fff' }}>Status: {statusFilter}</span>}
                {sportFilter  && <span className="ms-2 badge" style={{ background: '#1e3a5f', color: '#fff' }}>Sport: {sportFilter}</span>}
              </div>
            )}

            {/* ── Summary Cards ── */}
            <div className="row mb-4 g-3">
              <div className="col-6 col-md-2">
                <div className="card text-center" style={cardStyle('#334155')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Total Bets</div>
                    <div style={{ color: '#000', fontSize: '20px', fontWeight: '700' }}>{summary.totalRecords}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="card text-center" style={cardStyle('#f59e0b')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Open Bets</div>
                    <div style={{ color: '#d97706', fontSize: '20px', fontWeight: '700' }}>{summary.openBets}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="card text-center" style={cardStyle('#334155')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Players</div>
                    <div style={{ color: '#000', fontSize: '20px', fontWeight: '700' }}>{summary.uniquePlayers}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="card text-center" style={cardStyle('#334155')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Total Staked</div>
                    <div style={{ color: '#000', fontSize: '17px', fontWeight: '700' }}>৳{summary.totalBet?.toLocaleString()}</div>
                  </div>
                </div>
              </div>
              <div className="col-6 col-md-2">
                <div className="card text-center" style={cardStyle('#16a34a')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Players Won</div>
                    <div style={{ color: '#16a34a', fontSize: '17px', fontWeight: '700' }}>+৳{summary.totalWin?.toLocaleString()}</div>
                  </div>
                </div>
              </div>
              <div className="col-12 col-md-2">
                <div className="card text-center" style={cardStyle(summary.netProfitLoss >= 0 ? '#16a34a' : '#dc2626')}>
                  <div className="card-body p-3">
                    <div style={{ color: '#555', fontSize: '12px' }}>Net P/L</div>
                    <div style={{
                      fontSize: '18px', fontWeight: '800',
                      color: summary.netProfitLoss >= 0 ? '#16a34a' : '#dc2626',
                    }}>
                      {summary.netProfitLoss >= 0 ? '+' : ''}৳{summary.netProfitLoss?.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Match-wise Breakdown ── */}
            {sortedMatches.length > 0 && (
              <div className="card mb-4" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                <div className="card-body p-0">
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 className="mb-0" style={{ color: '#000' }}>🏏 Match-wise Breakdown</h6>
                  </div>
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover mb-0" style={{ color: '#000' }}>
                      <thead style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <tr>
                          {[
                            { key: 'match_name',  label: 'Match' },
                            { key: 'sport_name',  label: 'Sport' },
                            { key: 'totalBets',   label: 'Total Bets' },
                            { key: 'openCount',   label: 'Open' },
                            { key: 'playerCount', label: 'Players' },
                            { key: 'totalAmount', label: 'Total Staked' },
                            { key: 'totalWin',    label: 'Win Amt' },
                            { key: 'totalLoss',   label: 'Loss Amt' },
                            { key: 'netPL',       label: 'Net P/L' },
                            { key: 'lastBetAt',   label: 'Last Bet' },
                          ].map(col => (
                            <th key={col.key}
                              onClick={() => handleMatchSort(col.key)}
                              style={thStyle}>
                              {col.label} <MatchSortIcon field={col.key} />
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedMatches.map((m, i) => (
                          <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ fontWeight: '600', color: '#000', fontSize: '12px', maxWidth: '200px' }}>
                              {m.match_name || '—'}
                            </td>
                            <td style={{ fontSize: '12px', color: '#555' }}>{m.sport_name || '—'}</td>
                            <td style={{ color: '#000' }}>{m.totalBets}</td>
                            <td>
                              {m.openCount > 0
                                ? <span style={{ background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: '10px', fontSize: '11px', fontWeight: '600' }}>{m.openCount}</span>
                                : <span style={{ color: '#aaa' }}>0</span>
                              }
                            </td>
                            <td style={{ color: '#000' }}>{m.playerCount}</td>
                            <td style={{ color: '#000' }}>৳{m.totalAmount?.toLocaleString()}</td>
                            <td style={{ color: '#16a34a', fontWeight: '600' }}>+৳{m.totalWin?.toLocaleString()}</td>
                            <td style={{ color: '#dc2626', fontWeight: '600' }}>৳{m.totalLoss?.toLocaleString()}</td>
                            <td style={{ fontWeight: '700', color: m.netPL >= 0 ? '#16a34a' : '#dc2626' }}>
                              {m.netPL >= 0 ? '+' : ''}৳{m.netPL?.toLocaleString()}
                            </td>
                            <td style={{ color: '#555', fontSize: '12px', whiteSpace: 'nowrap' }}>
                              {m.lastBetAt ? formatDate(m.lastBetAt) : '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* ── Detail Table ── */}
            <div className="card" style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
              <div className="card-body p-0">
                <div style={{ padding: '12px 16px', borderBottom: '1px solid #e2e8f0' }}>
                  <h6 className="mb-0" style={{ color: '#000' }}>📋 Bet Details</h6>
                </div>
                {loading ? (
                  <div className="text-center p-5" style={{ color: '#555' }}>Loading...</div>
                ) : (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table table-hover mb-0" style={{ color: '#000' }}>
                      <thead style={{ borderBottom: '2px solid #e2e8f0' }}>
                        <tr>
                          <th style={thStyle} onClick={() => sortDetail('createdAt')}>
                            Date <SortIcon field="createdAt" />
                          </th>
                          <th style={thStyle}>Match</th>
                          <th style={thStyle}>Market</th>
                          <th style={thStyle}>B/L</th>
                          <th style={thStyle}>Odds</th>
                          <th style={thStyle}>Line</th>
                          <th style={thStyle} onClick={() => sortDetail('amount')}>
                            Amount <SortIcon field="amount" />
                          </th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Player</th>
                          <th style={thStyle}>Bet ID</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detailData.length > 0 ? (
                          detailData.map((item, idx) => (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ fontSize: '12px', color: '#000', whiteSpace: 'nowrap' }}>
                                {formatDate(item.createdAt)}
                              </td>
                              <td style={{ fontSize: '12px', color: '#000', maxWidth: '160px' }}>
                                {item.match_name || '—'}
                              </td>
                              <td style={{ fontSize: '11px', color: '#555', maxWidth: '160px' }}>
                                {item.market_name || '—'}
                              </td>
                              <td><BackLayBadge val={item.back_lay} /></td>
                              <td style={{ color: '#000', fontWeight: '600', fontSize: '12px' }}>
                                {item.odds || '—'}
                              </td>
                              <td style={{ color: '#555', fontSize: '12px' }}>
                                {item.line != null ? item.line : '—'}
                              </td>
                              <td style={{ color: '#000', fontWeight: '600' }}>
                                ৳{item.amount?.toLocaleString()}
                              </td>
                              <td><StatusBadge status={item.status} /></td>
                              <td style={{ fontSize: '11px', color: '#555' }}>
                                {item.ext_player_id?.substring(0, 10)}...
                              </td>
                              <td style={{ fontSize: '11px', color: '#555' }}>
                                {item.bet_id || '—'}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="10" className="text-center py-5" style={{ color: '#555' }}>
                              No bets found
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>

            {/* ── Pagination ── */}
            <MyPaginate
              breakLabel="..."
              nextLabel="next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="< previous"
              renderOnZeroPageCount={null}
              marginPagesDisplayed={2}
              forcePage={currentPage.current - 1}
            />

          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default LuckyExchangeSummery;
